/**
 * WHERE THEY ARE HEADED, PICKED FROM A LIST.
 *
 * Anthony, 2026-09-28: type a campground or an area and get the closest
 * matching campground first, or an address. The destination box on the
 * New trip form and on a trip used to take free text and place it only
 * when it named a town, so "Lake Perris" put no pin on the map and a
 * person had to type "33.85, -117.18" by hand. Now, after two letters, it
 * asks /api/places (src/data/places.ts) and offers what it found; picking
 * one fills the words AND the point, so the map pin is right with no
 * coordinates typed. Typing freely still works exactly as before: nothing
 * is required to come from the list.
 *
 * One widget for both screens, so the keys, the tap and the phone
 * behaviour cannot drift between them:
 *
 *   - 250ms after the last key, never a request per letter;
 *   - up and down move, Enter picks, Escape shuts, a tap picks;
 *   - an answer that arrives after the person typed on is thrown away,
 *     so a slow reply for "la" never replaces the list for "lake per";
 *   - ON A PHONE the field scrolls itself to the top of the screen when it
 *     is focused, so the list opens into the space ABOVE the keyboard
 *     rather than under it, and the list scrolls inside itself.
 *
 * `onPick` gets the place, or null when the person types over a picked
 * one, which is the caller's cue to forget the point it was holding.
 */

import { searchPlaces, type PlaceResult } from "../data/places.ts";
import { esc } from "./dom.ts";

const KIND: Record<PlaceResult["kind"], string> = { campground: "Campground", address: "Address", area: "Area" };

export function attachPlacePicker(inputId: string, onPick: (p: PlaceResult | null) => void): void {
  const input = document.getElementById(inputId) as HTMLInputElement | null;
  if (!input || input.dataset["picker"]) return;
  input.dataset["picker"] = "1";
  input.setAttribute("autocomplete", "off");
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-expanded", "false");

  const wrap = document.createElement("div");
  wrap.className = "ppick";
  input.parentElement?.insertBefore(wrap, input);
  wrap.appendChild(input);
  const list = document.createElement("div");
  list.className = "ppick-list";
  list.id = `${inputId}-places`;
  list.setAttribute("role", "listbox");
  list.hidden = true;
  wrap.appendChild(list);
  input.setAttribute("aria-controls", list.id);

  let results: PlaceResult[] = [];
  let at = -1;
  let asked = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let picked: string | null = null;

  const shut = () => { list.hidden = true; input.setAttribute("aria-expanded", "false"); input.removeAttribute("aria-activedescendant"); at = -1; };
  const draw = () => {
    if (!results.length) { shut(); return; }
    list.innerHTML = results.map((r, i) => `<button type="button" role="option" id="${list.id}-${i}" tabindex="-1"
        class="ppick-opt${i === at ? " on" : ""}" aria-selected="${i === at}" data-i="${i}">
        <span class="ppick-main"><b>${esc(r.label)}</b><i>${esc(r.sub)}${
          r.miles !== undefined ? ` &middot; ${esc(Math.round(r.miles))} mi` : ""}</i></span>
        <span class="ppick-kind ppick-${esc(r.kind)}">${esc(r.rv ? "RV park" : KIND[r.kind] ?? "Place")}</span></button>`).join("");
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    if (at >= 0) {
      input.setAttribute("aria-activedescendant", `${list.id}-${at}`);
      document.getElementById(`${list.id}-${at}`)?.scrollIntoView({ block: "nearest" });
    } else input.removeAttribute("aria-activedescendant");
  };
  const choose = (i: number) => {
    const r = results[i];
    if (!r) return;
    input.value = r.label;
    picked = r.label;
    shut();
    onPick(r);
  };

  input.addEventListener("input", () => {
    if (picked !== null && input.value !== picked) { picked = null; onPick(null); }
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) { results = []; shut(); return; }
    timer = setTimeout(async () => {
      const mine = ++asked;
      const found = await searchPlaces(q);
      if (mine !== asked || input.value.trim() !== q) return;
      results = found.slice(0, 8);
      at = -1;
      draw();
    }, 250);
  });
  input.addEventListener("keydown", (e) => {
    if (list.hidden || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); at = (at + 1) % results.length; draw(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); at = at <= 0 ? results.length - 1 : at - 1; draw(); }
    else if (e.key === "Enter" && at >= 0) { e.preventDefault(); choose(at); }
    else if (e.key === "Escape") { e.preventDefault(); shut(); }
  });
  /* pointerdown, not click: a click lands after the input's blur, and the
     blur has already shut the list by then. */
  list.addEventListener("pointerdown", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-i]");
    if (!b) return;
    e.preventDefault();
    choose(Number(b.dataset["i"]));
  });
  input.addEventListener("blur", () => setTimeout(shut, 120));
  input.addEventListener("focus", () => {
    if (window.matchMedia("(max-width: 640px)").matches) {
      setTimeout(() => input.scrollIntoView({ block: "start", behavior: "smooth" }), 280);
    }
    if (results.length && input.value.trim().length >= 2) draw();
  });
}
